import { useState } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import { motion, AnimatePresence } from "framer-motion";
import {
  LayoutDashboard,
  Grid3X3,
  Package,
  RefreshCw,
  Warehouse,
  Wallet,
  BarChart3,
  LogOut,
  Menu,
  ShoppingCart,
  Flame,
  Settings
} from "lucide-react";
import { Button } from "./ui/button";

const navItems = [
  { path: "/", icon: LayoutDashboard, label: "Dashboard" },
  { path: "/automat", icon: Grid3X3, label: "Automat" },
  { path: "/produkte", icon: Package, label: "Produkte" },
  { path: "/bestellungen", icon: ShoppingCart, label: "Bestellungen" },
  { path: "/auffuellen", icon: RefreshCw, label: "Auffüllen" },
  { path: "/lager", icon: Warehouse, label: "Lager" },
  { path: "/kasse", icon: Wallet, label: "Kasse" },
  { path: "/berichte", icon: BarChart3, label: "Berichte" },
  { path: "/einstellungen", icon: Settings, label: "Einstellungen" },
];

// Lünefire Logo Component
const LunefireLogo = ({ size = "md" }) => {
  const sizes = {
    sm: { flame: "w-6 h-6", text: "text-base" },
    md: { flame: "w-8 h-8", text: "text-lg" },
    lg: { flame: "w-10 h-10", text: "text-xl" }
  };
  const s = sizes[size] || sizes.md;
  
  return (
    <div className="flex items-center gap-2">
      <div className="relative">
        <Flame className={`${s.flame} text-[#DC2626]`} strokeWidth={2.5} />
        <Flame className={`${s.flame} text-[#FF8C00] absolute top-0 left-0 opacity-60`} strokeWidth={2.5} style={{ transform: 'scale(0.7) translate(20%, 30%)' }} />
      </div>
      <div>
        <span className={`font-heading font-bold ${s.text} lunefire-gradient-text`}>Lünefire</span>
        <span className="block text-[10px] text-slate-400 -mt-1 tracking-wider">SNACKSTATION</span>
      </div>
    </div>
  );
};

const Layout = () => {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Mobile overlay */}
      <AnimatePresence>
        {sidebarOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-40 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}
      </AnimatePresence>

      {/* Sidebar */}
      <aside
        className={`fixed lg:static inset-y-0 left-0 z-50 w-64 bg-[#1F2937] text-white transform transition-transform duration-300 lg:transform-none ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-5 border-b border-slate-700">
            <LunefireLogo size="md" />
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1">
            {navItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                end={item.path === "/"}
                onClick={() => setSidebarOpen(false)}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-3 rounded-md transition-all duration-200 ${
                    isActive
                      ? "bg-[#DC2626] text-white"
                      : "text-slate-300 hover:bg-slate-700 hover:text-white"
                  }`
                }
              >
                <item.icon className="w-5 h-5" strokeWidth={1.5} />
                <span className="font-medium">{item.label}</span>
              </NavLink>
            ))}
          </nav>

          {/* Logout */}
          <div className="p-4 border-t border-slate-700">
            <Button
              variant="ghost"
              className="w-full justify-start text-slate-300 hover:text-white hover:bg-slate-700"
              onClick={handleLogout}
              data-testid="logout-btn"
            >
              <LogOut className="w-5 h-5 mr-3" strokeWidth={1.5} />
              Abmelden
            </Button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-h-screen">
        {/* Top bar (mobile) */}
        <header className="lg:hidden sticky top-0 z-30 glass border-b border-slate-200 px-4 py-3">
          <div className="flex items-center justify-between">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setSidebarOpen(true)}
              data-testid="mobile-menu-btn"
            >
              <Menu className="w-6 h-6" />
            </Button>
            <LunefireLogo size="sm" />
            <div className="w-10" />
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 p-4 lg:p-8 overflow-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default Layout;
